/**
 * TelehealthByMember Component
 *
 * View telehealth session history grouped by member
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Users,
  Search,
  User,
  Video,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { telehealthSessionAPI } from '@/services/api/telehealth';
import { TelehealthSession } from './types';
import SessionStatusBadge from './SessionStatusBadge';

interface TelehealthByMemberProps {
  onBack: () => void;
}

interface MemberSessions {
  member_id: number;
  member_name: string;
  sessions: TelehealthSession[];
  total_sessions: number;
  completed_sessions: number;
  no_show_sessions: number;
}

export default function TelehealthByMember({ onBack }: TelehealthByMemberProps) {
  const { fmt } = useFormattedDate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMembers, setExpandedMembers] = useState<Set<number>>(new Set());

  // Fetch all sessions (past and future) and group by member
  const {
    data: sessions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['telehealth-all-sessions'],
    queryFn: () => telehealthSessionAPI.getAllSessions({ days_back: 90, days_ahead: 30 }),
  });

  // Group sessions by member
  const memberSessions: MemberSessions[] = useMemo(() => {
    if (!sessions) return [];

    const memberMap = new Map<number, MemberSessions>();

    sessions.forEach((session: TelehealthSession) => {
      const existing = memberMap.get(session.member_id);
      if (existing) {
        existing.sessions.push(session);
        existing.total_sessions++;
        if (session.status === 'completed') existing.completed_sessions++;
        if (session.status === 'no_show') existing.no_show_sessions++;
      } else {
        memberMap.set(session.member_id, {
          member_id: session.member_id,
          member_name: session.member_name || `Member #${session.member_id}`,
          sessions: [session],
          total_sessions: 1,
          completed_sessions: session.status === 'completed' ? 1 : 0,
          no_show_sessions: session.status === 'no_show' ? 1 : 0,
        });
      }
    });

    // Sort sessions within each member by date (newest first)
    memberMap.forEach((member) => {
      member.sessions.sort(
        (a, b) => new Date(b.scheduled_start_time).getTime() - new Date(a.scheduled_start_time).getTime()
      );
    });

    // Sort members by most recent session
    return Array.from(memberMap.values()).sort((a, b) => {
      const aLatest = new Date(a.sessions[0]?.scheduled_start_time || 0).getTime();
      const bLatest = new Date(b.sessions[0]?.scheduled_start_time || 0).getTime();
      return bLatest - aLatest;
    });
  }, [sessions]);

  // Filter by search query
  const filteredMembers = memberSessions.filter((member) => {
    if (!searchQuery) return true;
    return member.member_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const toggleMember = (memberId: number) => {
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Members List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Loader2 className="h-8 w-8 text-teal-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Loading sessions...</p>
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Failed to load sessions</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No members found matching your search' : 'No telehealth sessions yet'}
              </p>
            </div>
          ) : (
            filteredMembers.map((member) => (
              <div
                key={member.member_id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Member Header */}
                <button
                  onClick={() => toggleMember(member.member_id)}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                      <User className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {member.member_name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>{member.total_sessions} sessions</span>
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                          {member.completed_sessions} completed
                        </span>
                        {member.no_show_sessions > 0 && (
                          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <XCircle className="h-3.5 w-3.5" />
                            {member.no_show_sessions} no-show
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedMembers.has(member.member_id) ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {/* Sessions List (expandable) */}
                {expandedMembers.has(member.member_id) && (
                  <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {member.sessions.map((session) => (
                      <div
                        key={session.id}
                        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          {session.is_audio_only ? (
                            <Phone className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Video className="h-4 w-4 text-teal-600" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {fmt.custom(session.scheduled_start_time, 'MMM d, yyyy')}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {fmt.custom(session.scheduled_start_time, 'h:mm a')}
                              </span>
                              <SessionStatusBadge status={session.status} size="sm" />
                            </div>
                            {session.event_title && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {session.event_title}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                          {session.actual_duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {session.actual_duration_minutes} min
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
